import jsPDF from "jspdf";
import {
    brand,
    drawFrame,
    drawLogoPreserved,
    caseTransform,
    priceLabel,
} from "./pdfBrandHelpers";

/**
 * PDF "Piatti del Giorno" — A4, brand-aware.
 * Niente banda colorata in alto: l'intestazione vive dentro la cornice.
 * Logo con proporzioni originali. Nessuna riga di separazione tra i piatti.
 */
export function downloadDailyMenuPdf(menu, brandOpts = {}) {
    const B = brand(brandOpts);
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;
    const H = 297;
    const margin = 26;
    const contentW = W - margin * 2;
    const { primary, accent, ink, muted, paper } = B.rgb;
    const STYLE = B.style;
    const F = B.fonts;

    // 1) sfondo paper
    doc.setFillColor(...paper);
    doc.rect(0, 0, W, H, "F");

    // 2) cornice (stile dipendente)
    drawFrame(doc, W, H, margin, STYLE, B.rgb);

    // 3) Header (logo o nome) — dentro la cornice, niente banda
    let y = margin;

    if (B.useLogo && B.logoDataUrl) {
        const used = drawLogoPreserved(
            doc,
            B.logoDataUrl,
            W / 2,
            y,
            70,
            32,
        );
        y += (used || 0) + 6;
    } else if (menu.restaurant_name) {
        doc.setFont(F.subtitle.family, F.subtitle.weight);
        doc.setFontSize(14);
        doc.setTextColor(...primary);
        const rn = caseTransform(menu.restaurant_name, STYLE.nameCase);
        doc.text(rn, W / 2, y + 6, { align: "center" });
        y += 12;
    }

    // Spazio extra dopo il blocco logo/nome per evitare che il titolo si
    // accavalli (jsPDF usa la BASELINE come y, quindi il glifo del titolo
    // si estende verso l'alto di ~titleSize*0.7 mm dalla y di disegno).

    // Data
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    const today = new Date().toLocaleDateString("it-IT", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
    doc.text(today.toUpperCase(), W / 2, y, { align: "center" });
    y += 10;

    // Titolo principale
    doc.setFont(F.title.family, F.title.weight);
    doc.setFontSize(STYLE.titleSize);
    doc.setTextColor(...primary);
    // Sposta y alla baseline del titolo per evitare overlap con logo/data
    y += STYLE.titleSize * 0.42;
    const titleText = caseTransform(
        menu.title || "Piatti del Giorno",
        STYLE.nameCase === "upper" ? "upper" : "normal",
    );
    const titleLines = doc.splitTextToSize(titleText, contentW);
    titleLines.forEach((line, i) => {
        doc.text(line, W / 2, y + i * (STYLE.titleSize * 0.42), { align: "center" });
    });
    y += titleLines.length * (STYLE.titleSize * 0.42);

    // Mini decorazione sotto al titolo (solo classic & vintage, sottilmente)
    if (STYLE.id === "classic" || STYLE.id === "vintage") {
        doc.setDrawColor(...accent);
        doc.setLineWidth(STYLE.id === "vintage" ? 0.8 : 0.4);
        doc.line(W / 2 - 12, y + 2, W / 2 + 12, y + 2);
        y += 6;
    } else {
        y += 4;
    }

    // Messaggio per i clienti
    if (menu.message) {
        doc.setFont(F.subtitle.family, F.subtitle.weight);
        doc.setFontSize(11);
        doc.setTextColor(...muted);
        const msgLines = doc.splitTextToSize(menu.message, contentW - 30);
        msgLines.forEach((line, i) => {
            doc.text(line, W / 2, y + i * 5, { align: "center" });
        });
        y += msgLines.length * 5 + 6;
    } else {
        y += 4;
    }

    const ensureSpace = (need) => {
        if (y + need > H - 30) {
            doc.addPage();
            doc.setFillColor(...paper);
            doc.rect(0, 0, W, H, "F");
            drawFrame(doc, W, H, margin, STYLE, B.rgb);
            y = margin + 4;
        }
    };

    // Piatti — niente linee di separazione, solo whitespace
    (menu.dishes || []).forEach((d, idx) => {
        ensureSpace(STYLE.spacing + 12);

        const priceText = priceLabel(d.price_eur);
        const priceW = priceText
            ? (() => {
                  doc.setFont(F.price.family, F.price.weight);
                  doc.setFontSize(STYLE.nameSize);
                  return doc.getTextWidth(priceText) + 5;
              })()
            : 0;

        // nome piatto
        doc.setFont(F.dish.family, F.dish.weight);
        doc.setFontSize(STYLE.nameSize);
        doc.setTextColor(...primary);
        const nameMaxW = contentW - priceW;
        const nameText = caseTransform(d.name || "—", STYLE.nameCase);
        const nameLines = doc.splitTextToSize(nameText, nameMaxW);
        doc.text(nameLines, margin, y);

        if (priceText) {
            doc.setFont(F.price.family, F.price.weight);
            doc.setFontSize(STYLE.nameSize);
            doc.setTextColor(...accent);
            doc.text(priceText, W - margin, y, { align: "right" });
        }
        y += nameLines.length * (STYLE.nameSize * 0.42) + 1;

        if (d.description) {
            doc.setFont(F.subtitle.family, F.subtitle.weight);
            doc.setFontSize(10);
            doc.setTextColor(...ink);
            const descLines = doc.splitTextToSize(d.description, contentW);
            ensureSpace(descLines.length * 4.5 + 2);
            doc.text(descLines, margin, y);
            y += descLines.length * 4.5;
        }

        if (d.allergens?.length) {
            doc.setFont(F.allergens.family, F.allergens.weight);
            doc.setFontSize(8);
            doc.setTextColor(...muted);
            const allergText = `allergeni: ${d.allergens.join(" · ")}`;
            const allLines = doc.splitTextToSize(allergText, contentW);
            ensureSpace(allLines.length * 3.8 + 1);
            doc.text(allLines, margin, y);
            y += allLines.length * 3.8;
        }

        // niente linea di divisione — solo whitespace tra i piatti
        if (idx < (menu.dishes || []).length - 1) {
            y += STYLE.spacing;
        }
    });

    // Footer dentro alla cornice
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
        doc.setPage(p);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(7);
        doc.setTextColor(...muted);
        doc.text(
            "Allergeni: legge UE 1169/2011 — chiedere al personale per informazioni dettagliate",
            W / 2,
            H - 14,
            { align: "center" },
        );
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...accent);
        doc.text(`${p}/${pages}`, W - margin, H - 14, { align: "right" });
    }

    const safe = (menu.title || "Piatti del Giorno")
        .replace(/[^a-zA-Z0-9 _-]/g, "")
        .trim()
        .replace(/\s+/g, "_");
    doc.save(`${safe || "Piatti_del_Giorno"}.pdf`);
}
